import { ProfileOutlined, QuestionCircleOutlined, RocketOutlined } from '@ant-design/icons'
import { useRequest } from 'ahooks'
import { Col, Descriptions, Row, Tabs, Tooltip, Typography } from 'antd'
import { isNil } from 'lodash'
import moment from 'moment'
import React from 'react'
import { useTranslation } from 'react-i18next'

import { Editor, MosaicComponent, getPointPermission } from '@/components'
import { ParentRelations } from '@/components/parent-relations.component'
import { PriorityPopoverDescription } from '@/components/priority-popover.component'
import { ResourceUsers } from '@/components/resouce-users/resource-users.component'
import { ResourceOKR } from '@/components/resource-okr.component'
import { ResourceReport } from '@/modules/project/project-detail/project-summary'
import {
  PAGE_RESOURCE_POINTS_ENUM,
  PriorityEnum,
  Project,
  ProjectClassification,
  ResourceType,
  ResourceUser,
} from '@/schemas'

import { ProjectStages } from './project-stages.component'

const { Title } = Typography
const { TabPane } = Tabs

interface ProjectSummaryContainerProps {
  project: Project
  setUsers?: React.Dispatch<React.SetStateAction<ResourceUser[] | undefined>>
}

export const ProjectSummaryContainer: React.FC<ProjectSummaryContainerProps> = ({ project, setUsers }) => {
  const selfId = project.selfId
  const { t } = useTranslation()

  const { run } = useRequest(
    (data) => ({
      url: '/api/project/updateProject',
      method: 'patch',
      data: {
        ...data,
        selfId,
      },
    }),
    {
      manual: true,
      debounceInterval: 500,
      fetchKey: (params) => params.field,
    }
  )

  return (
    <Row gutter={[32, 0]}>
      <Col xs={24} md={16} className='children:mb-24px'>
        <Descriptions size='small' className='mt-8px'>
          {/* <Descriptions.Item label={t('department')}>
            <DepartmentName
              canRead={getPointPermission(PAGE_RESOURCE_POINTS_ENUM.概况_所属部门, project.permissions).canRead}
              departmentId={project.departmentId}
            />
          </Descriptions.Item> */}
          <Descriptions.Item
            label={
              <span>
                {t('devlop.detail.priority')}
                <PriorityPopoverDescription />
              </span>
            }
          >
            {!getPointPermission(PAGE_RESOURCE_POINTS_ENUM.概况_优先级, project.permissions).canRead ? (
              <MosaicComponent />
            ) : !isNil(project.priority) ? (
              t(`form.priority.${PriorityEnum[project.priority]}`)
            ) : (
              t('无')
            )}
          </Descriptions.Item>
          <Descriptions.Item label={t('devlop.detail.classification')}>
            {!getPointPermission(PAGE_RESOURCE_POINTS_ENUM.概况_分类, project.permissions).canRead ? (
              <MosaicComponent />
            ) : project.classification ? (
              t(ProjectClassification[project.classification])
            ) : null}
          </Descriptions.Item>
          <Descriptions.Item label={t('cycle')}>
            {!getPointPermission(PAGE_RESOURCE_POINTS_ENUM.概况_周期, project.permissions).canRead ? (
              <MosaicComponent />
            ) : (
              <>
                {project.startDate ? moment(project.startDate).format('YYYY-MM-DD') : 'N/A'} -{' '}
                {project.endDate ? moment(project.endDate).format('YYYY-MM-DD') : 'N/A'}
              </>
            )}
          </Descriptions.Item>
          {/* {project.projectType === ProjectType.RELEASE && (
            <>
              <Descriptions.Item label={t('release number')}>{project.releaseNumber}</Descriptions.Item>
              <Descriptions.Item label={t('release type')}>
                {t(`release type ${project.releaseType}`)}
              </Descriptions.Item>
              <Descriptions.Item label={t('release jira key')}>
                {project.releaseJiraKey && (
                  <a
                    href={`https://jira.agoralab.co/browse/${project.releaseJiraKey}`}
                    target='_blank'
                    rel='noopener noreferrer'
                  >
                    {project.releaseJiraKey}
                  </a>
                )}
              </Descriptions.Item>
            </>
          )} */}
        </Descriptions>

        <div>
          <Title level={4}>
            <ProfileOutlined className='mr-8px' />
            {t('resource.background')}
            <Tooltip title={t('devlop.form.background.tooltip')}>
              <QuestionCircleOutlined className='ml-4px text-gray-500 cursor-pointer' />
            </Tooltip>
          </Title>

          <Editor
            editorKey={project.selfId}
            editable={getPointPermission(PAGE_RESOURCE_POINTS_ENUM['概况_背景'], project.permissions).editable}
            canRead={getPointPermission(PAGE_RESOURCE_POINTS_ENUM['概况_背景'], project.permissions).canRead}
            content={project.background}
            onSave={(background) => run({ background })}
          />
        </div>

        <div>
          <Title level={4}>
            <RocketOutlined className='mr-8px' />
            {t('resource.goalDesc')}
            <Tooltip title={t('devlop.form.goal.tooltip')}>
              <QuestionCircleOutlined className='ml-4px text-gray-500 cursor-pointer' />
            </Tooltip>
          </Title>

          <Editor
            editorKey={project.selfId}
            className='mb-12px'
            editable={getPointPermission(PAGE_RESOURCE_POINTS_ENUM.概况_业务目标, project.permissions).editable}
            canRead={getPointPermission(PAGE_RESOURCE_POINTS_ENUM.概况_业务目标, project.permissions).canRead}
            content={project.goalDescription}
            onSave={(goalDescription) => run({ goalDescription })}
          />
        </div>

        <div>
          <Title level={4}>
            <ProfileOutlined className='mr-8px' />
            {t('resource.checkStandard')}
            <Tooltip title={t('devlop.form.standard.tooltip')}>
              <QuestionCircleOutlined className='ml-4px text-gray-500 cursor-pointer' />
            </Tooltip>
          </Title>

          <Editor
            editorKey={project.selfId}
            editable={
              getPointPermission(PAGE_RESOURCE_POINTS_ENUM['概况_交付物/验收标准'], project.permissions).editable
            }
            canRead={getPointPermission(PAGE_RESOURCE_POINTS_ENUM['概况_交付物/验收标准'], project.permissions).canRead}
            content={project.checkStandard}
            onSave={(checkStandard) => run({ checkStandard })}
          />
        </div>

        <div>
          <Title level={4}>
            <ProfileOutlined className='mr-8px' />
            {t('resource.challenge')}
            <Tooltip title={t('resource.challengeTooltip')}>
              <QuestionCircleOutlined className='ml-4px text-gray-500 cursor-pointer' />
            </Tooltip>
          </Title>

          <Editor
            editorKey={project.selfId}
            editable={getPointPermission(PAGE_RESOURCE_POINTS_ENUM.概况_挑战性, project.permissions).editable}
            canRead={getPointPermission(PAGE_RESOURCE_POINTS_ENUM.概况_挑战性, project.permissions).canRead}
            content={project.challenge}
            onSave={(challenge) => run({ challenge })}
          />
        </div>

        <ResourceOKR resource={project} isSummaryPage={true} />

        <ParentRelations resource={project} selfId={selfId} direction='up' type={ResourceType.PROJECT} />

        <ResourceUsers source={project} setUsers={setUsers} />
      </Col>

      <Col xs={24} md={8}>
        <ResourceReport resource={project} />

        <Tabs>
          <TabPane tab={t('project.stages')} key='stage'>
            <ProjectStages project={project} />
          </TabPane>
        </Tabs>
      </Col>
    </Row>
  )
}
